import { useEffect, useState } from "react";
import { api, TYPE_LABELS, orderBadge, mockupUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ShoppingBag, Wand2, AlertTriangle, CheckCircle2, MessageSquare, ArrowRight, Copy,
  Image as ImageIcon, Sparkles, Factory, ExternalLink, RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL = { needs_info: "Needs Info", in_design: "In Design", ready: "Ready", fulfilled: "Fulfilled" };
const NEXT = { needs_info: "in_design", in_design: "ready", ready: "fulfilled" };
const NEXT_LABEL = { needs_info: "Move to design", in_design: "Mark ready", ready: "Mark fulfilled" };

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [genBusy, setGenBusy] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const syncShopify = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/orders/sync-shopify");
      toast.success(data.synced ? `Added ${data.synced} personalized order${data.synced > 1 ? "s" : ""}` : "No new personalized orders found");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const load = () => api.get("/orders").then(({ data }) => {
    setOrders(data);
    setLoading(false);
    setSelected((s) => (s ? data.find((o) => o.id === s.id) || data[0] : data[0]));
  });
  useEffect(() => { load(); }, []);

  const parse = async (o) => {
    setParsing(true);
    toast.info("Extracting personalization details with AI…");
    try {
      const { data } = await api.post(`/orders/${o.id}/parse`);
      setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setSelected(data);
      toast.success(data.missing_fields?.length ? "Missing info detected — follow-up drafted" : "All details captured");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally { setParsing(false); }
  };

  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    const { data } = await api.patch(`/orders/${o.id}`, { status: next });
    setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setSelected(data);
    toast.success(`Order ${STATUS_LABEL[next]}`);
  };

  const sendToFulfillment = async (o) => {
    const { data } = await api.patch(`/orders/${o.id}`, { status: "ready" });
    setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    setSelected(data);
    toast.success("Ready-made order sent to fulfillment");
  };

  const genFile = async (o, mode) => {
    setGenBusy(mode);
    toast.info(mode === "ai" ? "Generating AI personalized design…" : "Rendering personalized print file…");
    try {
      const { data } = await api.post(`/orders/${o.id}/generate-personalized-file`, { mode });
      setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setSelected(data);
      toast.success("Personalized print file ready — preview it below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the file");
    } finally { setGenBusy(""); }
  };

  const sendPrintify = async (o) => {
    setSendBusy(true);
    toast.info("Sending to Printify → Shopify…");
    try {
      const { data } = await api.post(`/orders/${o.id}/send-to-printify`);
      setOrders((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setSelected(data);
      toast.success("Sent to Printify — order marked fulfilled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send to Printify failed");
    } finally { setSendBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="orders-page">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-blue-600">Personalization &amp; Fulfillment</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Orders needing customization</h1>
          <p className="text-sm text-zinc-500 mt-1">Pull personalized Shopify orders, generate the print file, and send to Printify.</p>
        </div>
        <button onClick={syncShopify} disabled={syncing} data-testid="sync-shopify-btn"
          className="h-9 px-3 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-60 text-sm font-medium flex items-center gap-1.5 transition-colors shrink-0">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-blue-600" />} Sync from Shopify
        </button>
      </div>

      {orders.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-lg py-16 text-center" data-testid="orders-empty">
          <ShoppingBag className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No personalized orders yet.</p>
          <p className="text-xs text-zinc-400 mt-1">Click <b>Sync from Shopify</b> to pull orders that have personalization text.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-[420px_1fr] gap-5">
        {/* List */}
        <div className="space-y-2">
          {orders.map((o) => (
            <button key={o.id} onClick={() => setSelected(o)} data-testid={`order-row-${o.id}`}
              className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                selected?.id === o.id ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500" : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-zinc-400">{o.order_number}</span>
                <Badge className={`text-[9px] border ${orderBadge[o.status]}`}>{STATUS_LABEL[o.status]}</Badge>
              </div>
              <div className="text-sm font-semibold mt-1 leading-tight">{o.product_title}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-zinc-500">{o.buyer_name}</span>
                <div className="flex items-center gap-1">
                  {o.personalization_required === false && <Badge className="text-[9px] bg-teal-100 text-teal-700 hover:bg-teal-100">Ready-made</Badge>}
                  <Badge variant="secondary" className="text-[9px]">{TYPE_LABELS[o.product_type]}</Badge>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        {selected ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="order-detail">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-mono text-zinc-400">{selected.order_number} · {selected.buyer_name}</span>
                <h2 className="font-display text-lg font-bold tracking-tight mt-0.5">{selected.product_title}</h2>
              </div>
              <Badge className={`text-[10px] border ${orderBadge[selected.status]}`}>{STATUS_LABEL[selected.status]}</Badge>
            </div>

            <div className="mt-4">
              <div className="overline text-zinc-400 mb-1.5 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Buyer note</div>
              <p className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-100 rounded-md p-3 leading-relaxed">{selected.note}</p>
            </div>

            {selected.personalization_required === false ? (
              <div className="mt-4 border border-teal-200 bg-teal-50 rounded-md p-4" data-testid="readymade-order-panel">
                <div className="flex items-center gap-2 text-teal-700 text-sm font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Ready-made — no buyer input needed
                </div>
                <p className="text-xs text-zinc-600 mt-1">This is a finished design. Nothing to personalize — route it straight to production.</p>
                {selected.status !== "fulfilled" && (
                  <button onClick={() => (selected.status === "ready" ? advance(selected) : sendToFulfillment(selected))} data-testid="send-fulfillment-btn"
                    className="mt-3 h-9 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-2 transition-colors">
                    {selected.status === "ready" ? "Mark fulfilled" : "Send to fulfillment"} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
            <>
            <div className="mt-4 flex items-center gap-2">
              <span className="overline text-zinc-400">Required:</span>
              {(selected.required_fields || []).map((f) => (
                <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{f}</span>
              ))}
            </div>

            {!selected.parsed ? (
              <button onClick={() => parse(selected)} disabled={parsing} data-testid="parse-order-btn"
                className="mt-5 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium flex items-center gap-2 transition-colors">
                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {parsing ? "Extracting…" : "Auto-extract personalization"}
              </button>
            ) : (
              <div className="mt-5 space-y-4">
                <div>
                  <div className="overline text-zinc-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Extracted details</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selected.extracted || {}).map(([k, v]) => (
                      <div key={k} className={`text-xs rounded-md px-2.5 py-1.5 border ${v ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                        <div className="text-[10px] text-zinc-400 uppercase">{k}</div>
                        <div className={v ? "text-zinc-800 font-medium" : "text-rose-500 italic"}>{v || "missing"}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {selected.missing_fields?.length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                    <div className="flex items-center gap-1.5 text-amber-700 text-xs font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Missing: {selected.missing_fields.join(", ")}</div>
                    {selected.followup_message && (
                      <div className="mt-2 flex items-start gap-2">
                        <p className="text-xs text-zinc-600 bg-white rounded p-2 border border-amber-100 flex-1">{selected.followup_message}</p>
                        <button onClick={() => { navigator.clipboard.writeText(selected.followup_message); toast.success("Follow-up copied"); }} data-testid="copy-followup-btn" className="text-amber-600 hover:text-amber-700 shrink-0 mt-1"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                )}

                {selected.order_type !== "wholesale" && (
                  <div className="border border-zinc-200 rounded-lg p-3.5" data-testid="personalization-file-panel">
                    <div className="overline text-zinc-400 mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-blue-600" /> Personalized print file</div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => genFile(selected, "overlay")} disabled={!!genBusy} data-testid="gen-overlay-btn"
                        className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium flex items-center gap-1.5 transition-colors">
                        {genBusy === "overlay" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Generate file
                      </button>
                      <button onClick={() => genFile(selected, "ai")} disabled={!!genBusy} data-testid="gen-ai-btn"
                        className="h-9 px-3 rounded-md border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-60 text-xs font-medium flex items-center gap-1.5 transition-colors">
                        {genBusy === "ai" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI version
                      </button>
                    </div>
                    {selected.personalized_design_path && (
                      <div className="mt-3 flex items-start gap-3">
                        <img src={mockupUrl(selected.personalized_design_path)} alt="Personalized print file"
                          className="w-24 h-24 rounded-md object-contain bg-[repeating-conic-gradient(#eee_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] border border-zinc-200" data-testid="personalized-preview" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-zinc-500 mb-2">Printing: <span className="font-medium text-zinc-800 whitespace-pre-line">{selected.personalization_text}</span></p>
                          {selected.printify_product_id ? (
                            <a href={selected.printify_url} target="_blank" rel="noreferrer" data-testid="order-printify-link"
                              className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Sent to Printify <ExternalLink className="w-3 h-3" /></a>
                          ) : (
                            <button onClick={() => sendPrintify(selected)} disabled={sendBusy} data-testid="send-printify-btn"
                              className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium flex items-center gap-1.5 transition-colors">
                              {sendBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Factory className="w-3.5 h-3.5" />} Send to Printify
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selected.status !== "fulfilled" && (
                  <button onClick={() => advance(selected)} data-testid="advance-order-btn"
                    className="h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-2 transition-colors">
                    {NEXT_LABEL[selected.status]} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            </>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-zinc-300 rounded-lg flex flex-col items-center justify-center py-24">
            <ShoppingBag className="w-10 h-10 text-zinc-300" />
            <p className="text-sm text-zinc-500 mt-3">Select an order to process personalization.</p>
          </div>
        )}
      </div>
    </div>
  );
}
